import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiRequest } from '../api/client'
import { useAuth } from '../auth/AuthContext'

const SSO_DOMAIN = 'brooklinen.com'

export default function LoginPage() {
  const { user, loading, setTokenAndRefresh } = useAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [step, setStep] = useState('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState(searchParams.get('error') || '')
  const [submitting, setSubmitting] = useState(false)
  const [notice, setNotice] = useState('')

  const ssoFormRef = useRef(null)

  useEffect(() => {
    if (!loading && user) navigate('/trade_applications', { replace: true })
  }, [loading, user, navigate])

  const isSsoEmail = email.toLowerCase().endsWith(`@${SSO_DOMAIN}`)

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setNotice('')

    if (!email) {
      setError('Email is required')
      return
    }

    if (isSsoEmail) {
      ssoFormRef.current?.submit()
      return
    }

    setSubmitting(true)
    try {
      await apiRequest('/api/v1/auth/otp', {
        method: 'POST',
        body: { email },
        auth: false
      })
      setStep('otp')
      setNotice(`If ${email} is registered, we sent a 6-digit code.`)
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleCodeSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const { token } = await apiRequest('/api/v1/auth/otp/verify', {
        method: 'POST',
        body: { email, code },
        auth: false
      })
      await setTokenAndRefresh(token)
      navigate('/trade_applications', { replace: true })
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const resetFlow = () => {
    setStep('email')
    setCode('')
    setError('')
    setNotice('')
  }

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-10 sm:py-16">
      <div className="card p-6 sm:p-8">
        <h1 className="text-xl font-semibold text-slate-900 mb-2">Sign in</h1>
        <p className="text-sm text-slate-600 mb-6">
          Brooklinen staff use Google; external reviewers use an email code.
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-800">
            {error}
          </div>
        )}
        {notice && (
          <div className="mb-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
            {notice}
          </div>
        )}

        {step === 'email' && (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <label className="label" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                className="input"
                placeholder="name@brooklinen.com"
              />
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full">
              {submitting ? 'Sending…' : isSsoEmail ? 'Continue with Google' : 'Send me a code'}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={handleCodeSubmit} className="space-y-4">
            <div>
              <label className="label" htmlFor="code">6-digit code</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                autoComplete="one-time-code"
                required
                className="input text-center text-2xl tracking-[0.5em] font-mono"
                placeholder="••••••"
              />
            </div>
            <button type="submit" disabled={submitting || code.length !== 6} className="btn-primary w-full">
              {submitting ? 'Verifying…' : 'Sign in'}
            </button>
            <button type="button" onClick={resetFlow} className="w-full text-sm text-slate-600 hover:text-slate-900">
              Use a different email
            </button>
          </form>
        )}
      </div>

      <form
        ref={ssoFormRef}
        action="/api/v1/auth/google_oauth2"
        method="post"
        className="hidden"
      >
        <input
          type="hidden"
          name="authenticity_token"
          value={document.querySelector('meta[name="csrf-token"]')?.content || ''}
        />
      </form>
    </div>
  )
}
